import { useEffect, useRef, useState } from 'react';
import { api, ApiError, formatUsd } from '../lib/api';
import { Button, Field, Input, Select } from './ui';

/** What /encoders/options offers - the server owns the lists. */
export interface EncoderOptions {
  resolutions: string[];
  /** The above plus 1080i: encodes only, not RTMP. */
  encodeResolutions?: string[];
  frameRates: string[];
  videoCodecs: string[];
  profiles: Record<string, string[]>;
  audioCodecs: string[];
  audioBitrates: Record<string, number[]>;
  defaultEncode: Encode;
}

export interface Encode {
  label: string;
  resolution: string;
  fps: string;
  mbps: number;
  dualFlows: boolean;
  codec: string;
  profile: string;
  audioCodec: string;
  audioKbps: number;
  /** The SRT outputs each of its flows is made with; missing means the one RECEIVER. */
  outputs?: { main?: FlowOutputSpec[]; backup?: FlowOutputSpec[] };
}

/** An SRT output on a flow - a listener the receiver pulls from, or a caller pushing to them. */
export interface FlowOutputSpec {
  name: string;
  mode: 'listener' | 'caller';
  port: number;
  host?: string;
  streamId?: string;
  minLatency?: number;
  enabled?: boolean;
}

/** What a template puts where a receiver's or platform's address was - launch refuses it. */
export const PLACEHOLDER_HOST = '0.0.0.0';

/** What every output flow gets unless told otherwise - mirrors DEFAULT_OUTPUT on the server. */
export const DEFAULT_OUTPUT: FlowOutputSpec = { name: 'RECEIVER', mode: 'listener', port: 7000, minLatency: 1000 };

export const newOutput = (): FlowOutputSpec => ({ name: '', mode: 'listener', port: 7001, host: '', streamId: '', minLatency: 1000 });

/** A copy to add alongside the original: same settings, a name that is free. */
export function duplicateOutput(o: FlowOutputSpec, taken: string[]): FlowOutputSpec {
  const base = o.name.replace(/-\d+$/, '');
  let n = 2;
  while (taken.includes(`${base}-${n}`)) n++;
  return { ...o, name: `${base}-${n}`.slice(0, 40), enabled: undefined };
}

/**
 * Name, mode, port, receiver and latency for one SRT output. Used to add,
 * edit and duplicate outputs on a running encoder, and for each output in the
 * launch form.
 */
export function OutputFields({ value, onChange, nameLocked }: {
  value: FlowOutputSpec;
  onChange: (v: FlowOutputSpec) => void;
  nameLocked?: boolean;
}) {
  const set = (patch: Partial<FlowOutputSpec>) => onChange({ ...value, ...patch });
  const caller = value.mode === 'caller';
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      <Field label="Name">
        {nameLocked
          ? <div className="py-2 text-sm text-ink-secondary">{value.name}</div>
          : <Input value={value.name} maxLength={40} onChange={(e) => set({ name: e.target.value.replace(/[^A-Za-z0-9_-]/g, '') })} placeholder="TVNZ" />}
      </Field>
      <Field label="Mode">
        <Select value={value.mode} onChange={(e) => set({ mode: e.target.value as FlowOutputSpec['mode'] })}>
          <option value="listener">Listener - they pull</option>
          <option value="caller">Caller - we push</option>
        </Select>
      </Field>
      <Field label="Port">
        <Input type="number" min={1024} max={65535} value={value.port} onChange={(e) => set({ port: Number(e.target.value) })} />
      </Field>
      {caller ? (
        <>
          <Field label="Their host" error={value.host === PLACEHOLDER_HOST ? 'Put in their address' : null}>
            <Input value={value.host ?? ''} onChange={(e) => set({ host: e.target.value.trim() })} placeholder="receiver.example.com" />
          </Field>
          <Field label="Stream ID">
            <Input value={value.streamId ?? ''} onChange={(e) => set({ streamId: e.target.value })} placeholder="optional" />
          </Field>
        </>
      ) : null}
      <Field label="Latency ms">
        <Input type="number" min={20} max={15000} value={value.minLatency ?? 1000} onChange={(e) => set({ minLatency: Number(e.target.value) })} />
      </Field>
    </div>
  );
}

/** Every output one flow will be made with, in the launch form. */
export function OutputListEditor({ value, onChange }: {
  value: FlowOutputSpec[];
  onChange: (v: FlowOutputSpec[]) => void;
}) {
  return (
    <div className="space-y-2">
      {value.length === 0 && <div className="text-xs text-ink-muted">No outputs.</div>}
      {value.map((o, i) => (
        <div key={i} className="space-y-1 rounded bg-plane/60 p-2">
          <OutputFields value={o} onChange={(v) => onChange(value.map((x, j) => (j === i ? v : x)))} />
          <div className="flex gap-3 text-xs">
            {o.enabled === false && <span className="text-ink-muted">made switched off</span>}
            <button type="button" className="text-series-1 hover:underline"
              onClick={() => onChange([...value.slice(0, i + 1), duplicateOutput(o, value.map((x) => x.name)), ...value.slice(i + 1)])}>
              duplicate
            </button>
            <button type="button" className="text-ink-muted hover:text-critical" onClick={() => onChange(value.filter((_, j) => j !== i))}>remove</button>
          </div>
        </div>
      ))}
      {value.length < 20 && (
        <button type="button" className="text-xs text-series-1 hover:underline" onClick={() => onChange([...value, newOutput()])}>+ add SRT output</button>
      )}
    </div>
  );
}

export interface RtmpDestination { url: string; streamKey: string }

export interface Rtmp {
  id?: string;
  label: string;
  resolution: string;
  fps: string;
  mbps: number;
  audioPair: number;
  audioKbps: number;
  destinations: RtmpDestination[];
}

export interface CostEstimate {
  mainPerHour: number;
  rtmp: Record<string, number>;
  idlePerHour: number;
  parts: { group: string; name: string; usdPerHour: number }[];
  unpriced: boolean;
  /** When a recording was asked for with the estimate: what it adds while recording. */
  recording?: { perHour: number; gbPerHour: number; idlePerHour: number };
}

export const CODEC_LABEL: Record<string, string> = { H264: 'H.264', H265: 'HEVC' };
const AUDIO_LABEL: Record<string, string> = { AAC: 'AAC', AC3: 'Dolby AC-3', EAC3: 'Dolby E-AC-3', MP2: 'MPEG-1 Layer II' };

export const newRtmp = (): Rtmp => ({
  label: '', resolution: '1080p', fps: '50', mbps: 6, audioPair: 1, audioKbps: 128,
  destinations: [{ url: '', streamKey: '' }],
});

/** Codec, profile and audio for one encode - shared by launch and edit. */
export function CodecFields({ opts, value, onChange }: {
  opts: EncoderOptions;
  value: Encode;
  onChange: (patch: Partial<Encode>) => void;
}) {
  return (
    <>
      <Field label="Codec">
        <Select
          value={value.codec}
          onChange={(e) => onChange({ codec: e.target.value, profile: opts.profiles[e.target.value][opts.profiles[e.target.value].includes('MAIN') ? opts.profiles[e.target.value].indexOf('MAIN') : 0] })}
        >
          {opts.videoCodecs.map((c) => <option key={c} value={c}>{CODEC_LABEL[c] ?? c}</option>)}
        </Select>
      </Field>
      <Field label="Profile">
        <Select value={value.profile} onChange={(e) => onChange({ profile: e.target.value })}>
          {(opts.profiles[value.codec] ?? []).map((p) => <option key={p} value={p}>{p.replace('_', ' ').toLowerCase()}</option>)}
        </Select>
      </Field>
      <Field label="Audio">
        <Select
          value={value.audioCodec}
          onChange={(e) => {
            const codec = e.target.value;
            const rates = opts.audioBitrates[codec];
            onChange({ audioCodec: codec, audioKbps: rates.includes(value.audioKbps) ? value.audioKbps : rates[Math.floor(rates.length / 2)] });
          }}
        >
          {opts.audioCodecs.map((c) => <option key={c} value={c}>{AUDIO_LABEL[c] ?? c}</option>)}
        </Select>
      </Field>
      <Field label="Audio kbit/s">
        <Select value={value.audioKbps} onChange={(e) => onChange({ audioKbps: Number(e.target.value) })}>
          {(opts.audioBitrates[value.audioCodec] ?? []).map((k) => <option key={k} value={k}>{k}</option>)}
        </Select>
      </Field>
    </>
  );
}

/** Picture settings - resolution, frame rate, bitrate. */
export function PictureFields({ opts, value, onChange, maxMbps = 40, resolutions = opts.resolutions }: {
  opts: EncoderOptions;
  resolutions?: string[];
  value: { resolution: string; fps: string; mbps: number };
  onChange: (patch: { resolution?: string; fps?: string; mbps?: number }) => void;
  maxMbps?: number;
}) {
  return (
    <>
      <Field label="Resolution">
        <Select value={value.resolution} onChange={(e) => onChange({ resolution: e.target.value })}>
          {resolutions.map((r) => <option key={r} value={r}>{r}</option>)}
        </Select>
      </Field>
      <Field label="Frame rate">
        <Select value={value.fps} onChange={(e) => onChange({ fps: e.target.value })}>
          {opts.frameRates.map((f) => <option key={f} value={f}>{f} {value.resolution === '1080i' ? 'fields/s' : 'fps'}</option>)}
        </Select>
      </Field>
      <Field label="Mbit/s">
        <Input
          type="number" min={0.5} max={maxMbps} step={0.5}
          value={value.mbps}
          onChange={(e) => onChange({ mbps: Number(e.target.value) })}
        />
      </Field>
    </>
  );
}

/**
 * One RTMP encode: its picture, which pair it sends, and where it goes. The
 * stream key is a password as far as the platform is concerned, so it is typed
 * into a password box and masked on the encoder page.
 */
export function RtmpEditor({ opts, value, onChange, audioPairs, originalLabel }: {
  opts: EncoderOptions;
  value: Rtmp;
  onChange: (v: Rtmp) => void;
  audioPairs: number;
  /** The label it has now, when editing one that exists: changing it rebuilds the small channel. */
  originalLabel?: string;
}) {
  const set = (patch: Partial<Rtmp>) => onChange({ ...value, ...patch });
  const setDest = (i: number, patch: Partial<RtmpDestination>) =>
    set({ destinations: value.destinations.map((d, j) => (j === i ? { ...d, ...patch } : d)) });

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-6">
        <Field label="Name" error={originalLabel && value.label !== originalLabel ? 'Renaming rebuilds this RTMP channel (a minute or two off air)' : null}>
          <Input
            value={value.label}
            onChange={(e) => set({ label: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '') })}
            placeholder="YOUTUBE"
            maxLength={20}
          />
        </Field>
        <PictureFields opts={opts} value={value} onChange={set} maxMbps={20} />
        <Field label="Audio pair">
          <Select value={value.audioPair} onChange={(e) => set({ audioPair: Number(e.target.value) })}>
            {Array.from({ length: audioPairs }, (_, i) => i + 1).map((p) => <option key={p} value={p}>Pair {p}</option>)}
          </Select>
        </Field>
        <Field label="AAC kbit/s">
          <Select value={value.audioKbps} onChange={(e) => set({ audioKbps: Number(e.target.value) })}>
            {(opts.audioBitrates.AAC ?? []).map((k) => <option key={k} value={k}>{k}</option>)}
          </Select>
        </Field>
      </div>
      {value.destinations.map((d, i) => (
        <div key={i} className="grid items-end gap-3 sm:grid-cols-[2fr_2fr_auto]">
          <Field label={i === 0 ? 'RTMP server URL' : `Destination ${i + 1} URL`}
            error={/^rtmps?:\/\/0\.0\.0\.0[:/]/i.test(d.url) ? "Put in the server - 0.0.0.0 is the template's stand-in" : null}>
            <Input value={d.url} onChange={(e) => setDest(i, { url: e.target.value.trim() })} placeholder="rtmp://a.rtmp.youtube.com/live2" />
          </Field>
          <Field label="Stream key">
            <Input type="password" autoComplete="off" value={d.streamKey} onChange={(e) => setDest(i, { streamKey: e.target.value.trim() })} />
          </Field>
          <div className="pb-1">
            {value.destinations.length > 1 && (
              <Button variant="ghost" onClick={() => set({ destinations: value.destinations.filter((_, j) => j !== i) })}>Remove</Button>
            )}
          </div>
        </div>
      ))}
      <Button
        variant="ghost"
        onClick={() => set({ destinations: [...value.destinations, { url: '', streamKey: '' }] })}
        disabled={value.destinations.length >= 10}
      >
        + Another destination at the same settings
      </Button>
      <p className="text-xs text-ink-muted">
        H.264 high profile and AAC - what RTMP carries. Taken straight from the ingest flows, with the same
        failover, so it is one encode from the source and starts, stops and is re-keyed without touching
        the main encoder. Destinations here share one encode and restart together; give a destination its
        own RTMP encode to control it separately.
      </p>
    </div>
  );
}

/** The hourly estimate, part by part - shown while building and on the encoder. */
export function CostBreakdown({ est, rtmpLabels }: { est: CostEstimate; rtmpLabels?: Record<string, string> }) {
  const groups: [string, string][] = [['channel', 'MediaLive channel'], ['flows', 'MediaConnect flows'], ['recording', 'Recording - only while it records']];
  const rtmpIds = Object.keys(est.rtmp);
  const rtmpTotal = rtmpIds.reduce((s, id) => s + est.rtmp[id], 0);
  return (
    <div className="space-y-3 text-sm">
      {groups.map(([g, title]) => {
        const rows = est.parts.filter((p) => p.group === g);
        if (!rows.length) return null;
        return (
          <div key={g}>
            <div className="text-xs text-ink-muted">{title}</div>
            {rows.map((p) => (
              <div key={p.name} className="flex justify-between gap-3 text-xs">
                <span className="text-ink-secondary">{p.name}</span>
                <span className="tabular">{formatUsd(p.usdPerHour, 3)}/hr</span>
              </div>
            ))}
          </div>
        );
      })}
      {rtmpIds.length > 0 && (
        <div>
          <div className="text-xs text-ink-muted">RTMP encodes - each only while it runs</div>
          {est.parts.filter((p) => p.group.startsWith('rtmp:')).map((p) => (
            <div key={p.group} className="flex justify-between gap-3 text-xs">
              <span className="text-ink-secondary">{rtmpLabels?.[p.group.slice(5)] ?? p.name}</span>
              <span className="tabular">{formatUsd(p.usdPerHour, 3)}/hr</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-between gap-3 border-t border-grid pt-2">
        <span>Running{rtmpIds.length ? ', with every RTMP encode on' : ''}{est.recording ? ', not recording' : ''}</span>
        <strong className="tabular">{formatUsd(est.mainPerHour + rtmpTotal, 2)}/hr</strong>
      </div>
      {est.recording && (
        <div className="flex justify-between gap-3">
          <span>While recording{est.recording.gbPerHour ? ` (about ${est.recording.gbPerHour} GB an hour to S3)` : ''}</span>
          <strong className="tabular">{formatUsd(est.mainPerHour + rtmpTotal + est.recording.perHour, 2)}/hr</strong>
        </div>
      )}
      <div className="flex justify-between gap-3 text-xs text-ink-muted">
        <span>Stopped, not deleted</span>
        <span className="tabular">{formatUsd(est.idlePerHour + (est.recording?.idlePerHour ?? 0), 3)}/hr</span>
      </div>
      <p className="text-xs text-ink-muted">
        From AWS's price list for MediaLive and this account's bill for flows. Data sent to receivers
        and RTMP platforms is billed on top, by the gigabyte.
        {est.unpriced && ' Some parts could not be priced and are counted as nothing.'}
      </p>
    </div>
  );
}

// ===========================================================================
// Media clips
// ===========================================================================

export interface Clip {
  id?: string;
  name: string;
  key: string;
  durationSec: number | null;
}

interface Upload { file: File; name: string; progress: number; error: string | null }

export const formatClipTime = (sec: number | null | undefined) => {
  if (sec == null || !Number.isFinite(sec)) return '—';
  const s = Math.max(0, Math.round(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/** The file's length, read by the browser - null if it cannot play it. */
function readDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const v = document.createElement('video');
    const url = URL.createObjectURL(file);
    const done = (d: number | null) => { URL.revokeObjectURL(url); resolve(d); };
    v.preload = 'metadata';
    v.onloadedmetadata = () => done(Number.isFinite(v.duration) ? v.duration : null);
    v.onerror = () => done(null);
    setTimeout(() => done(null), 10_000);
    v.src = url;
  });
}

/** PUT straight to S3 on a presigned URL, with progress - the dashboard host never carries the file. */
function putFile(url: string, file: File, contentType: string, onProgress: (p: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`upload refused (${xhr.status})`)));
    xhr.onerror = () => reject(new Error('upload failed - check the connection'));
    xhr.send(file);
  });
}

/**
 * Pick clips, upload each straight to S3, and list what is ready. Uploading
 * starts as soon as a file is chosen; `onBusy` holds the launch button until
 * every upload has finished.
 */
export function ClipUploader({ value, onChange, onBusy, disabled }: {
  value: Clip[];
  onChange: (clips: Clip[]) => void;
  onBusy?: (busy: boolean) => void;
  disabled?: boolean;
}) {
  const [uploads, setUploads] = useState<Upload[]>([]);
  const latest = useRef(value);
  latest.current = value;

  useEffect(() => { onBusy?.(uploads.some((u) => !u.error)); }, [uploads, onBusy]);

  const setUpload = (file: File, patch: Partial<Upload>) =>
    setUploads((us) => us.map((u) => (u.file === file ? { ...u, ...patch } : u)));

  async function add(files: FileList | null) {
    if (!files) return;
    for (const file of Array.from(files)) {
      const name = file.name.replace(/\.[^.]+$/, '');
      setUploads((us) => [...us, { file, name, progress: 0, error: null }]);
      void (async () => {
        try {
          // Read alongside the upload, not before it: a browser that is slow
          // to open the file (a background tab) must not hold the upload up.
          const duration = readDuration(file);
          const target = await api.post<{ key: string; url: string; contentType: string }>(
            '/encoders/media/upload', { filename: file.name, size: file.size },
          );
          await putFile(target.url, file, target.contentType, (p) => setUpload(file, { progress: p }));
          const durationSec = await duration;
          onChange([...latest.current, { name, key: target.key, durationSec }]);
          setUploads((us) => us.filter((u) => u.file !== file));
        } catch (err) {
          setUpload(file, { error: err instanceof ApiError || err instanceof Error ? err.message : 'upload failed' });
        }
      })();
    }
  }

  return (
    <div className="space-y-2">
      {value.map((c, i) => (
        <div key={c.key} className="flex items-center gap-2 rounded bg-plane/60 px-2 py-1.5 text-sm">
          <Input
            className="min-w-0 flex-1"
            value={c.name}
            disabled={disabled}
            onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
          />
          <span className="w-12 text-right text-xs tabular text-ink-muted">{formatClipTime(c.durationSec)}</span>
          {!disabled && (
            <button className="text-xs text-ink-muted hover:text-critical" onClick={() => onChange(value.filter((_, j) => j !== i))}>remove</button>
          )}
        </div>
      ))}
      {uploads.map((u) => (
        <div key={u.name + u.file.size} className="space-y-1 rounded border border-grid px-2 py-1.5 text-xs">
          <div className="flex justify-between gap-2">
            <span className="truncate">{u.file.name}</span>
            {u.error
              ? <button className="text-ink-muted hover:underline" onClick={() => setUploads((us) => us.filter((x) => x !== u))}>dismiss</button>
              : <span className="tabular text-ink-muted">{Math.round(u.progress * 100)}%</span>}
          </div>
          {u.error
            ? <div style={{ color: 'var(--color-critical)' }}>{u.error}</div>
            : <div className="h-1 overflow-hidden rounded bg-plane"><div className="h-full bg-series-1" style={{ width: `${u.progress * 100}%` }} /></div>}
        </div>
      ))}
      {!disabled && (
        <label className="inline-flex cursor-pointer items-center gap-2 text-xs text-series-1 hover:underline">
          <input type="file" multiple accept=".mp4,.m4v,.ts,video/mp4,video/mp2t" className="hidden"
            onChange={(e) => { void add(e.target.files); e.target.value = ''; }} />
          + upload clips
        </label>
      )}
    </div>
  );
}
